import { useState, useCallback, useEffect, useContext, Suspense, useMemo } from "react";
import useRouteParam from "../../../../../hooks/useRouteParam";
import { ADMIN_DASHBOARD_HEADER } from "../../../../../constants/caption/dashboardHeader";
import { AuthContext, UIContext } from "../../../../../contexts";
import TabPanel from "../../../../shared/tabPanelComponent/TabPanel";
import a11yProps from "../../../../../constants/tabPanelProps/ayProps";
import { LazyVEBEnrollmentsGrid } from "../../..";
import { url, port } from "../../../../../config/config";

import { Box, Tabs, Tab } from "@material-ui/core";
import { trackPromise } from "react-promise-tracker";
import { manualDateComparator } from "../../../../../utils/commonFunctions/date";
import CircularLoader from "../../../../shared/loader/CircularLoader";
import axiosInstance from "../../../../../utils/commonFunctions/axiosInstance";
import { getPermissionsFromGroupOwner } from "../../../../../utils/getPermissionsFromGroupOwner";

const VEBEnrollments = () => {
	const [tabValue, setTabValue] = useState(0);
	const { setDashboardHeader } = useContext(UIContext);
	const [loading, setLoading] = useState(true);
	const queryGroupNumber = useRouteParam("gn");
	const [activeVEBEnrollmens, setActiveVEBEnrollments] = useState([]);
	const [expiredVEBEnrollments, setExpiredVEBEnrollments] = useState([]);
	const [upcomingVEBEnrollments, setUpcomingVEBEnrollments] = useState([]);

	const [employeeFilterValue, setEmployeeFilterValue] = useState("");
	const [firstRender, setFirstRender] = useState(false);

	// eslint-disable-next-line @typescript-eslint/ban-types
	const handleLabelChange = (event, value) => {
		setTabValue(value);
	};

	// console.log(queryGroupNumber);

	const { user, setUser } = useContext(AuthContext);

	const allVEBEnrollmentPermission = user
		? getPermissionsFromGroupOwner(user, Number(queryGroupNumber))?.plan_control_menu.all_veb_enrollments_sub.self
		: null;

	console.log("allVEBEnrollmentPermission", allVEBEnrollmentPermission);
	// Permission check for tabs
	const showActive = allVEBEnrollmentPermission?.active_tab?.view;
	const showUpcoming = allVEBEnrollmentPermission?.upcoming_tab?.view;
	const showExpired = allVEBEnrollmentPermission?.expired_tab?.view;

	// Build list of visible tabs
	const visibleTabs = useMemo(() => {
		const tabs = [];
		let index = 0;

		if (!showActive && !showUpcoming && !showExpired) return [];

		if (showActive) {
			tabs.push({ label: "Active", index: index++ });
		}
		if (showUpcoming) {
			tabs.push({ label: "Upcoming", index: index++ });
		}
		if (showExpired) {
			tabs.push({ label: "Expired", index: index++ });
		}
		return tabs;
	}, [showActive, showUpcoming, showExpired]);

	const getVEBEnrollments = useCallback(async () => {
		setLoading(true);
		const filter = {};
		if (employeeFilterValue && employeeFilterValue.length > 0) {
			filter["employee_SSN"] = employeeFilterValue;
		}

		const token = localStorage.getItem("@jwt");
		const api_url = `${url}:${port}/api/v1/member/open-enrollment/VEB/${queryGroupNumber}`;

		try {
			const response = await trackPromise(
				axiosInstance.get(api_url, { headers: { Authorization: `Bearer ${token}` }, params: filter })
			);

			if (response.status === 200) {
				const _VEBOpenEnrollments = response.data.result;
				const _activeVEBEnrollments = [];
				const _expiredVEBEnrollments = [];
				const _upcomingVEBEnrollments = [];

				const currentDate = new Date();

				for (let index = 0; index < _VEBOpenEnrollments.length; index++) {
					const enrollment = _VEBOpenEnrollments[index];
					const startDate = enrollment.start_date;
					const startDateComparison = manualDateComparator(startDate, currentDate);

					if (enrollment.end_date) {
						const endDate = enrollment.end_date;

						if (startDateComparison <= 0) {
							const endDateComparison = manualDateComparator(endDate, currentDate);
							if (endDateComparison >= 0) {
								_activeVEBEnrollments.push(enrollment);
							} else {
								_expiredVEBEnrollments.push(enrollment);
							}
						} else {
							_upcomingVEBEnrollments.push(enrollment);
						}
					} else {
						if (startDateComparison <= 0) {
							_activeVEBEnrollments.push(enrollment);
						} else {
							_upcomingVEBEnrollments.push(enrollment);
						}
					}
				}

				setActiveVEBEnrollments([..._activeVEBEnrollments]);
				setUpcomingVEBEnrollments([..._upcomingVEBEnrollments]);
				setExpiredVEBEnrollments([..._expiredVEBEnrollments]);
			}
		} catch (error) {
			console.error("Error fetching VEB enrollments:", error);
			// Optionally show user-facing error feedback here
		} finally {
			setLoading(false);
		}
	}, [queryGroupNumber, employeeFilterValue]);

	useEffect(() => {
		setDashboardHeader(ADMIN_DASHBOARD_HEADER.all_VEB_enrollments);
	}, [setDashboardHeader]);

	useEffect(() => {
		// queryGroupNumber !== null ? getVEBEnrollments() : null;
		if (queryGroupNumber !== null) {
			getVEBEnrollments();
			setFirstRender(true);
		}
	}, [getVEBEnrollments, queryGroupNumber]);

	useEffect(() => {
		if (firstRender) {
			getVEBEnrollments();
		}
	}, [employeeFilterValue]);

	return (
		<div className="all-veb-enrollment-container">
			{loading && <CircularLoader />}
			<Box sx={{ width: "100%" }}>
				<Box sx={{ borderBottom: 1, borderColor: "divider" }}>
					<Tabs
						value={tabValue}
						onChange={handleLabelChange}
						aria-label="basic tabs example"
						TabIndicatorProps={{
							style: {
								border: "none",
								backgroundColor: "#558b2f"
							}
						}}
						className="glb-tab-panel"
					>
						{visibleTabs.map((tab, i) => (
							<Tab key={i} label={tab.label} className="glb-tab-label" {...a11yProps(tab.index)} />
						))}
					</Tabs>
				</Box>
				{visibleTabs.map((tab, i) => {
					let content = null;

					switch (tab.label) {
						case "Active":
							content = (
								<LazyVEBEnrollmentsGrid
									status="ACTIVE"
									gridData={activeVEBEnrollmens}
									setEmployeeFilterValue={setEmployeeFilterValue}
									employeeFilterValue={employeeFilterValue}
								/>
							);
							break;
						case "Upcoming":
							content = (
								<LazyVEBEnrollmentsGrid
									status="UPCOMING"
									gridData={upcomingVEBEnrollments}
									setEmployeeFilterValue={setEmployeeFilterValue}
									employeeFilterValue={employeeFilterValue}
								/>
							);
							break;
						case "Expired":
							content = (
								<LazyVEBEnrollmentsGrid
									status="EXPIRED"
									gridData={expiredVEBEnrollments}
									setEmployeeFilterValue={setEmployeeFilterValue}
									employeeFilterValue={employeeFilterValue}
								/>
							);
							break;
					}

					return (
						<TabPanel key={i} value={tabValue} index={i}>
							<Suspense fallback={<div />}>{content}</Suspense>
						</TabPanel>
					);
				})}
			</Box>
		</div>
	);
};

export default VEBEnrollments;
